/**
 * Direkte Reservierung Widget Loader
 *
 * Loads and initializes reservation widgets for the chat interface.
 * Dynamically loads the reservation React app and renders widgets.
 */

const RESERVATION_API_URL =
  window.RESERVATION_API_URL || "https://reservation.pension.de/api/v1";
const RESERVATION_WIDGET_URL =
  window.RESERVATION_WIDGET_URL ||
  "https://reservation.pension.de/assets/tenant.js";

/**
 * Safely parse JWT payload in browser context.
 *
 * @param {string} token
 * @returns {Record<string, any>|null}
 */
function parseJwtPayload(token) {
  try {
    const payload = String(token || "").split(".")[1];
    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );

    return JSON.parse(decoded);
  } catch (e) {
    return null;
  }
}

/**
 * Remove stale tenant auth data from localStorage when token is expired.
 *
 * @param {{ tokenKey?: string, tenantIdKey?: string }} [options]
 */
function cleanupExpiredTenantToken(options = {}) {
  const tokenKey = options.tokenKey || "dr_token";
  const tenantIdKey = options.tenantIdKey || "dr_tenant_id";

  const savedToken = localStorage.getItem(tokenKey);
  if (!savedToken) {
    return;
  }

  const payload = parseJwtPayload(savedToken);
  if (!payload || typeof payload.exp !== "number") {
    return;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  if (payload.exp <= nowInSeconds) {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(tenantIdKey);
  }
}

/**
 * Refresh tenant JWT when it's close to expiry.
 *
 * @param {{ tokenKey?: string, tenantIdKey?: string, refreshWindowSeconds?: number }} [options]
 * @returns {Promise<{ refreshed: boolean, reason: string, token?: string }>}
 */
async function refreshTenantTokenIfNeeded(options = {}) {
  const tokenKey = options.tokenKey || "dr_token";
  const tenantIdKey = options.tenantIdKey || "dr_tenant_id";
  const refreshWindowSeconds = Number.isFinite(
    Number(options.refreshWindowSeconds)
  )
    ? Number(options.refreshWindowSeconds)
    : 900;

  const currentToken = localStorage.getItem(tokenKey);
  if (!currentToken) {
    return { refreshed: false, reason: "missing_token" };
  }

  const payload = parseJwtPayload(currentToken);
  if (!payload || typeof payload.exp !== "number") {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(tenantIdKey);
    return { refreshed: false, reason: "invalid_token" };
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const secondsToExpiry = payload.exp - nowInSeconds;

  if (secondsToExpiry <= 0) {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(tenantIdKey);
    return { refreshed: false, reason: "expired_token" };
  }

  if (secondsToExpiry > refreshWindowSeconds) {
    return { refreshed: false, reason: "not_needed", token: currentToken };
  }

  try {
    const response = await fetch(`${RESERVATION_API_URL}/tenant/auth/refresh`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(tenantIdKey);
        return { refreshed: false, reason: "unauthorized" };
      }

      return { refreshed: false, reason: `refresh_failed_${response.status}` };
    }

    const refreshPayload = await response.json();
    const refreshedToken = refreshPayload?.data?.token;

    if (!refreshedToken || typeof refreshedToken !== "string") {
      return { refreshed: false, reason: "missing_new_token" };
    }

    localStorage.setItem(tokenKey, refreshedToken);

    return {
      refreshed: true,
      reason: "refreshed",
      token: refreshedToken,
    };
  } catch (e) {
    console.error("Failed to refresh tenant token", e);
    return { refreshed: false, reason: "network_error" };
  }
}

/**
 * Load the reservation widget script (production).
 *
 * Contains no Vite/HMR/dev-server logic — the dev variant lives in
 * `reservation-widgets.dev.js` and is wired in by tenant-page.dev.js when
 * webpack.config.js selects the dev entry (Encore not in production).
 */
function loadReservationWidgets() {
  if (document.getElementById("dr-widgets-script")) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = "dr-widgets-script";
    script.src = RESERVATION_WIDGET_URL;
    script.type = "module";
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * Render the reservation button in a chat.
 *
 * @param {HTMLElement} container - Container element
 * @param {Object} options - Options
 * @param {number} options.chatRequestId - Chat request ID
 * @param {string} options.jwt - JWT token
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 */
function renderReservationButton(container, options) {
  loadReservationWidgets()
    .then(() => {
      const button = document.createElement("dr-reservation-button");
      button.setAttribute("chat-request-id", options.chatRequestId);
      button.setAttribute("api-url", RESERVATION_API_URL);
      if (options.jwt) {
        button.setAttribute("jwt", options.jwt);
      }

      if (options.onSuccess) {
        button.addEventListener("success", (e) =>
          options.onSuccess(e.detail.reservationUuid)
        );
      }
      if (options.onError) {
        button.addEventListener("error", (e) =>
          options.onError(e.detail.error)
        );
      }

      container.appendChild(button);
    })
    .catch(console.error);
}

/**
 * Render the score display.
 *
 * @param {HTMLElement} container - Container element
 * @param {Object} options - Options
 * @param {string} options.userType - 'landlord' or 'tenant'
 * @param {string|number} options.userId - User ID
 */
function renderScoreDisplay(container, options) {
  loadReservationWidgets()
    .then(() => {
      const score = document.createElement("dr-score-display");
      score.setAttribute("user-type", options.userType);
      score.setAttribute("user-id", options.userId);
      score.setAttribute("api-url", RESERVATION_API_URL);

      container.appendChild(score);
    })
    .catch(console.error);
}

/**
 * Check if a landlord has reservations enabled.
 *
 * @param {number} accountId - Account ID
 * @param {string} portal - Portal code
 * @returns {Promise<boolean>}
 */
async function isReservationEnabled(accountId, portal) {
  try {
    const response = await fetch(
      `${RESERVATION_API_URL}/landlord/settings?account_id=${accountId}&portal=${portal}`
    );
    const data = await response.json();
    return data.data?.settings?.is_enabled ?? false;
  } catch (e) {
    console.error("Failed to check reservation status", e);
    return false;
  }
}

/**
 * Check whether direct reservation is allowed for a tenant email.
 *
 * @param {string} email
 * @returns {Promise<{canDirectReserve: boolean, score: number|null, isPositiveScore: boolean, daysUntilReset?: number|null, blockedMessage?: string|null}>}
 */
async function checkTenantDirectReservationEligibility(email) {
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();

  if (!normalizedEmail) {
    return {
      canDirectReserve: false,
      score: null,
      isPositiveScore: false,
    };
  }

  try {
    const response = await fetch(
      `${RESERVATION_API_URL}/tenant/score-check?email=${encodeURIComponent(normalizedEmail)}`,
      { method: "GET" }
    );

    if (!response.ok) {
      return {
        canDirectReserve: false,
        score: null,
        isPositiveScore: false,
      };
    }

    const payload = await response.json();
    const data = payload?.data || payload;
    const canDirectReserve = Boolean(data?.can_direct_reserve);
    const isPositiveScore =
      typeof data?.is_positive_score === "boolean"
        ? data.is_positive_score
        : Boolean(canDirectReserve);
    const score = Number.isFinite(Number(data?.score))
      ? Number(data.score)
      : null;
    const daysUntilReset = Number.isFinite(Number(data?.days_until_reset))
      ? Math.max(0, Math.floor(Number(data.days_until_reset)))
      : null;
    const blockedMessage =
      typeof data?.blocked_message === "string" ? data.blocked_message : null;

    return {
      canDirectReserve,
      score,
      isPositiveScore,
      daysUntilReset,
      blockedMessage,
    };
  } catch (e) {
    console.error("Failed to check tenant direct reservation eligibility", e);
    return {
      canDirectReserve: false,
      score: null,
      isPositiveScore: false,
    };
  }
}

// Export for global access
window.DirecteReservierung = {
  loadWidgets: loadReservationWidgets,
  renderButton: renderReservationButton,
  renderScore: renderScoreDisplay,
  isEnabled: isReservationEnabled,
  checkTenantEligibility: checkTenantDirectReservationEligibility,
  parseJwtPayload,
  cleanupExpiredTenantToken,
  refreshTenantTokenIfNeeded,
  apiUrl: RESERVATION_API_URL,
};

export {
  loadReservationWidgets,
  renderReservationButton,
  renderScoreDisplay,
  isReservationEnabled,
  checkTenantDirectReservationEligibility,
  parseJwtPayload,
  cleanupExpiredTenantToken,
  refreshTenantTokenIfNeeded,
};
